import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./styles.module.scss";
import { Button, Stack, Box } from "@mui/material";
import SearchBar from "./SearchBar";
import ProfileMenu from "./ProfileMenu";
import LandingHeaderLogo from "../assets/LandingHeaderLogo.svg";
import white_text_logo from "./dgi_logo_white.png";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { useUserStateContext } from "../App";
import { useNavigate } from "react-router-dom";
import { AppBar } from "@mui/material";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import SubHeader from "./SubHeader";
import { Backdrop } from "@mui/material";
import { useSearchBox } from "react-instantsearch";

function SignInButton() {
  const navigate = useNavigate();
  return (
    <Button
      variant="text"
      sx={{
        color: "#FF7F11",
        borderColor: "#FF7F11",
        borderWidth: "2px",
        borderStyle: "solid",
        borderRadius: "12px",
        marginRight: "20px",
        fontSize: "1.1em",
        textTransform: "none",
        "&:hover": {
          backgroundColor: "rgba(255, 255, 255, 0.2)",
          opacity: 0.8,
        },
        typography: "inherit",
      }}
      onClick={() => navigate("/auth")}
    >
      Sign In / Sign Up
    </Button>
  );
}

const Header = React.memo(function Header({
  windowWidth,
  setHeaderHeight = null,
  landingPage = false,
}) {
  const navigate = useNavigate();
  const { isUserAuthed, userCreds } = useUserStateContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const headerRef = useRef(null);
  const [showBackdrop, setShowBackdrop] = useState(false);
  const { refine, query, clear } = useSearchBox();
  const [searchInputValue, setSearchInputValue] = useState(query);

  const [mainMenuAnchorEl, setMainMenuAnchorEl] = useState(null);
  const mainMenuOpen = Boolean(mainMenuAnchorEl);

  useEffect(() => {
    if (setHeaderHeight) {
      if (headerRef.current) {
        setHeaderHeight(headerRef.current.clientHeight);
      }
    }
  }, [windowWidth]);

  const handleClearSearchBox = useCallback(() => {
    clear();
    setSearchInputValue("");
  }, [clear, setSearchInputValue]);

  const handleMainMenuClick = (event) => {
    if (menuOpen) {
      handleMainMenuClose();
    } else {
      setMainMenuAnchorEl(event.currentTarget);
      setMenuOpen(true);
    }
  };

  const handleMainMenuClose = () => {
    setMainMenuAnchorEl(null);
    setMenuOpen(false);
  };

  const displayAuthedFeatures = isUserAuthed && userCreds.shopName;
  const logo = landingPage ? LandingHeaderLogo : white_text_logo;

  const renderMenuIcon = !menuOpen ? (
    <MenuIcon className={styles.iconBlack} />
  ) : (
    <CloseIcon className={styles.iconBlack} />
  );

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        className={`${!landingPage && "mb-[1rem]"} bg-white`}
      >
        {isUserAuthed ? (
          <>
            <Box
              className={`min-h-header relative
              flex items-center justify-center
              ${landingPage ? "bg-grayscaleWhite" : "bg-denim"}`}
              ref={headerRef}
            >
              <Box
                className={`
                max-w-[1600px] w-full 
                flex flex-row items-center justify-between gap-[12px]`}
                sx={{
                  zIndex: showBackdrop && 1301, // needs to supercede the backdrop
                }}
              >
                <Stack
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                  spacing={2}
                  className="flex w-full max-w-575 gap-3"
                >
                  <Box
                    className="flex cursor-pointer pl-[1rem]"
                    sx={{
                      transition: "transform 0.3s",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                    onClick={() => navigate("/")}
                  >
                    <img src={logo} alt="Company logo" className="h-2.5R" />
                  </Box>
                  {displayAuthedFeatures && (
                    <SearchBar
                      setShowBackdrop={setShowBackdrop}
                      inputValue={searchInputValue}
                      setInputValue={setSearchInputValue}
                      refine={refine}
                    />
                  )}
                </Stack>
                <Stack direction="row" justifyContent="flex-end">
                  <Stack
                    direction="row"
                    className="hidden lg:flex gap-2.5R pr-[2.75rem]"
                  >
                    <a
                      id="shop-page-header-link"
                      href="/shop"
                      className={`flex items-center gap-[3px]
                      whitespace-nowrap cursor-pointer
                      hover:opacity-80 no-underline`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/shop");
                      }}
                    >
                      <ShoppingBagOutlinedIcon
                        className="text-grayscaleWhite"
                        fontSize="medium"
                      />
                      <div className="mt-[4px] text-grayscaleWhite">Shop</div>
                    </a>
                    <a
                      id="cart-page-header-link"
                      href="/cart"
                      className={`flex items-center gap-[3px]
                      whitespace-nowrap cursor-pointer
                      hover:opacity-80 no-underline`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/cart");
                      }}
                    >
                      <ShoppingCartOutlinedIcon
                        className="text-grayscaleWhite"
                        fontSize="medium"
                      />
                      <div className="mt-[4px] text-grayscaleWhite">Cart</div>
                    </a>
                    <a
                      id="history-page-header-link"
                      href="/order-history"
                      className={`flex items-center gap-[6px]
                      whitespace-nowrap cursor-pointer
                      hover:opacity-80 no-underline`}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate("/order-history");
                      }}
                    >
                      <ImportContactsIcon
                        className="text-grayscaleWhite mt-[2px]"
                        fontSize="small"
                      />
                      <div className="mt-[4px] text-grayscaleWhite">
                        History
                      </div>
                    </a>
                  </Stack>
                  <ProfileMenu />
                </Stack>
              </Box>
            </Box>
            <SubHeader handleClearSearchBox={handleClearSearchBox} />
          </>
        ) : (
          <>
            <Box
              className={`min-h-header relative
              flex items-center justify-center ${
                landingPage ? "bg-grayscaleWhite" : "bg-denim"
              }`}
              ref={headerRef}
            >
              <Box className="max-w-[2400px] w-full flex flex-row items-center justify-between gap-[12px]">
                <Stack
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                  spacing={2}
                  className="flex w-full max-w-575 gap-3"
                >
                  <Box
                    className="flex cursor-pointer pl-[1rem]"
                    sx={{
                      transition: "transform 0.3s",
                      "&:hover": {
                        transform: "scale(1.05)",
                      },
                    }}
                    onClick={() => navigate("/")}
                  >
                    <img src={logo} alt="Company logo" className="h-2.5R" />
                  </Box>
                  {displayAuthedFeatures && (
                    <SearchBar
                      setShowBackdrop={setShowBackdrop}
                      inputValue={searchInputValue}
                      setInputValue={setSearchInputValue}
                      refine={refine}
                    />
                  )}
                </Stack>
                {windowWidth <= 800 ? (
                  <>
                    <div
                      className={styles.hamburgerMenuWrapper}
                      onClick={handleMainMenuClick}
                    >
                      {renderMenuIcon}
                    </div>
                    <Menu
                      anchorEl={mainMenuAnchorEl}
                      open={mainMenuOpen}
                      onClose={handleMainMenuClose}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                      }}
                    >
                      <MenuItem
                        onClick={() => {
                          navigate("/");
                          handleMainMenuClose();
                        }}
                        className="text-wolfGrey"
                      >
                        Home
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          navigate("/product-demo");
                          handleMainMenuClose();
                        }}
                        className="text-wolfGrey"
                      >
                        Product
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          navigate("/our-story");
                          handleMainMenuClose();
                        }}
                        className="text-wolfGrey"
                      >
                        Our Story
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          navigate("/auth");
                          handleMainMenuClose();
                        }}
                        className="text-wolfGrey"
                      >
                        Sign In / Sign Up
                      </MenuItem>
                    </Menu>
                  </>
                ) : (
                  <>
                    {landingPage && (
                      <div className={styles.landingPageLinks}>
                        <div
                          className={styles.textLink}
                          onClick={() => navigate("/")}
                        >
                          <span className="text-wolfGrey">Home</span>
                        </div>
                        <div
                          className={styles.textLink}
                          onClick={() => navigate("/product-demo")}
                        >
                          <span className="text-wolfGrey">Product</span>
                        </div>
                        <div
                          className={styles.textLink}
                          onClick={() => navigate("/our-story")}
                        >
                          <span>Our Story</span>
                        </div>
                        <SignInButton />
                      </div>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </>
        )}
      </AppBar>
      <Backdrop
        sx={{
          backgroundColor: "rgba(88, 88, 88, 0.2)",
          zIndex: (theme) => theme.zIndex.modal,
        }}
        open={showBackdrop}
      />
    </>
  );
});

export default Header;
